import { useEffect, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from "react";
import { MESSAGE_MAX, type AvailableCommand, type CreateMessagePoll, type GifAttachment, type UserProfile, type UserSummary } from "@atrium/shared";
import { api } from "../../lib/api";
import { cn } from "../../lib/cn";
import { usePrefs } from "../../lib/prefs";
import { Avatar } from "../../components/ui/Avatar";
import { IconButton } from "../../components/ui/Button";
import { BotTag, NameTags } from "../../components/UserBadges";
import { IconEye, IconEyeOff, IconGif, IconHourglass, IconImage, IconPoll, IconSend, IconSlashCommand, IconX } from "../../components/icons";
import { UploadPreviews } from "../posts/Composer";
import { useUploads, type Uploader } from "../posts/useUploads";
import { useMe } from "../auth/AuthProvider";
import { activeFormats, applyFormat, escapedUsernames, expandRawRefs, type FormatEdit, type FormatKind, type RawRefLookup } from "./format";
import { FormatToolbar, PREVIEW_SHORTCUT, formatShortcut } from "./FormatToolbar";
import { GifPicker } from "./GifPicker";
import { MessageItem } from "./MessageItem";
import { PollComposer } from "./PollComposer";
import { onComposerInsert } from "./composerBus";
import { useServerConfig } from "./serverConfig";
import type { ChatMessage, MessageActions } from "./types";
import { CommandForm, CommandPicker, matchCommands, useCommands, type CommandContext } from "./commands";
import { imageFiles, isFileDrag, pastedImages } from "../../lib/clipboardFiles";
import { toast } from "../../components/ui/Toast";

const drafts = new Map<string, string>();
/** Preview stays on across conversations for the session once turned on. */
let previewOn = false;

export interface ComposerSend {
  content: string;
  mediaIds: string[];
  gif?: GifAttachment;
  poll?: CreateMessagePoll;
}

const PREVIEW_ACTIONS: MessageActions = {
  canReply: false,
  canReact: false,
  canPin: false,
  canModerate: false,
  onReply: () => {},
  onEdit: async () => {},
  onDelete: () => {},
};

function TypingLine({ names }: { names: string[] }) {
  if (!names.length) return <div className="h-5" />;
  const text =
    names.length === 1 ? `${names[0]} is typing…` : names.length === 2 ? `${names[0]} and ${names[1]} are typing…` : names.length === 3 ? `${names.join(", ")} are typing…` : "Several people are typing…";
  return (
    <div className="flex h-5 items-center gap-1.5 px-1 text-xs text-fg-2" aria-live="polite">
      <span className="flex gap-0.5" aria-hidden>
        {[0, 1, 2].map((i) => (
          <span key={i} className="size-1 animate-bounce rounded-full bg-fg-2" style={{ animationDelay: `${i * 120}ms` }} />
        ))}
      </span>
      <span className="truncate">{text}</span>
    </div>
  );
}

export function ChatComposer({
  draftKey,
  placeholder,
  disabledReason,
  replyTo,
  onCancelReply,
  onSend,
  allowAttachments,
  uploader,
  allowPolls = false,
  allowGifs = false,
  mentionSource,
  onTyping,
  typingNames,
  slowModeSeconds = 0,
  commands,
  rawRefs,
}: {
  draftKey: string;
  placeholder: string;
  disabledReason?: string | null;
  replyTo: ChatMessage | null;
  onCancelReply: () => void;
  onSend: (input: ComposerSend) => void;
  allowAttachments: boolean;
  /** Upload attachments this way instead (encrypted conversations encrypt each image first). */
  uploader?: Uploader;
  /** Channel composers can attach polls (DMs can't). */
  allowPolls?: boolean;
  /** Show the GIF picker (when the server has a provider). The parent must forward `gif` in onSend. */
  allowGifs?: boolean;
  mentionSource?: (q: string) => Promise<UserSummary[]>;
  onTyping?: () => void;
  typingNames: string[];
  slowModeSeconds?: number;
  /** Channel composers: offer the community's slash commands when the text starts with "/". */
  commands?: CommandContext;
  /** Roles and channels that `\@Role` / `\#channel` turn into raw syntax (people are looked up). */
  rawRefs?: Pick<RawRefLookup, "roles" | "channels">;
}) {
  const me = useMe();
  const [text, setText] = useState(() => drafts.get(draftKey) ?? "");
  const [cooldown, setCooldown] = useState(0);
  const [mention, setMention] = useState<{ q: string; start: number; items: UserSummary[]; index: number } | null>(null);
  const [sel, setSel] = useState<{ start: number; end: number } | null>(null);
  const [pointerDown, setPointerDown] = useState(false);
  const [preview, setPreview] = useState(previewOn);
  const [gifOpen, setGifOpen] = useState(false);
  const [pollOpen, setPollOpen] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const lastTyping = useRef(0);
  // Sends wait for `\@name` lookups; chaining them keeps messages in order.
  const sendChain = useRef<Promise<void>>(Promise.resolve());
  const uploads = useUploads({ max: 5, allowVideo: false, upload: uploader });
  const prefs = usePrefs();
  const config = useServerConfig();
  const gifsEnabled = allowGifs && !!config.data?.gifs;

  // Slash commands: a picker while typing "/name", then command mode with an input per option.
  const slashTyped = !!commands && text.startsWith("/");
  const { data: commandList } = useCommands(commands?.communityId, slashTyped);
  const [command, setCommand] = useState<AvailableCommand | null>(null);
  const [cmdIndex, setCmdIndex] = useState(0);
  const [pickerDismissed, setPickerDismissed] = useState(false);
  const [sentTo, setSentTo] = useState<AvailableCommand | null>(null);
  const matches = slashTyped && !command && !pickerDismissed && commandList ? matchCommands(commandList, text) : null;
  const picker = matches?.length ? matches : null;
  const pickerIndex = picker ? Math.min(cmdIndex, picker.length - 1) : 0;

  useEffect(() => {
    setCmdIndex(0);
    if (!text.startsWith("/")) setPickerDismissed(false);
  }, [text]);

  useEffect(() => {
    setCommand(null);
    setSentTo(null);
  }, [draftKey]);

  useEffect(() => {
    if (!sentTo) return;
    const t = setTimeout(() => setSentTo(null), 6000);
    return () => clearTimeout(t);
  }, [sentTo]);

  const enterCommand = (c: AvailableCommand) => {
    setCommand(c);
    setText("");
    drafts.delete(draftKey);
    setMention(null);
  };

  const exitCommand = () => {
    setCommand(null);
    requestAnimationFrame(() => ref.current?.focus());
  };

  useEffect(() => {
    setText(drafts.get(draftKey) ?? "");
    uploads.reset();
    setMention(null);
    setSel(null);
    if (window.matchMedia("(pointer: fine)").matches) ref.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  useEffect(() => {
    drafts.set(draftKey, text);
    const el = ref.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
    }
  }, [text, draftKey]);

  useEffect(() => {
    if (replyTo) ref.current?.focus();
  }, [replyTo]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (!pointerDown) return;
    const up = () => setPointerDown(false);
    window.addEventListener("pointerup", up);
    return () => window.removeEventListener("pointerup", up);
  }, [pointerDown]);

  /** Replace a range keeping the browser's undo history (falls back to a plain state update). */
  const replaceRange = (edit: FormatEdit) => {
    const el = ref.current;
    if (!el) return;
    const next = text.slice(0, edit.start) + edit.text + text.slice(edit.end);
    if (next.length > MESSAGE_MAX) return;
    el.focus();
    el.setSelectionRange(edit.start, edit.end);
    let ok = false;
    try {
      ok = edit.text ? document.execCommand("insertText", false, edit.text) : document.execCommand("delete");
    } catch {
      ok = false;
    }
    if (!ok || el.value !== next) {
      setText(next);
      requestAnimationFrame(() => {
        ref.current?.setSelectionRange(edit.selStart, edit.selEnd);
        syncSelection();
      });
      return;
    }
    el.setSelectionRange(edit.selStart, edit.selEnd);
    syncSelection();
  };

  const format = (kind: FormatKind) => {
    const el = ref.current;
    if (!el) return;
    replaceRange(applyFormat(el.value, el.selectionStart, el.selectionEnd, kind));
  };

  const syncSelection = () => {
    const el = ref.current;
    if (!el || document.activeElement !== el) return setSel(null);
    setSel({ start: el.selectionStart, end: el.selectionEnd });
  };

  // "Mention" from a user's menu: insert at the caret.
  useEffect(
    () =>
      onComposerInsert((insert) => {
        const el = ref.current;
        if (!el || disabledReason) return;
        const start = document.activeElement === el ? el.selectionStart : el.value.length;
        const end = document.activeElement === el ? el.selectionEnd : el.value.length;
        const lead = start > 0 && !/\s/.test(el.value[start - 1]) ? " " : "";
        const piece = lead + insert;
        replaceRange({ start, end, text: piece, selStart: start + piece.length, selEnd: start + piece.length });
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [text, disabledReason],
  );

  const detectMention = (value: string, caret: number) => {
    if (!mentionSource) return;
    const before = value.slice(0, caret);
    // `\@name` autocompletes too (it sends the raw <@id> instead of a ping).
    const m = /(^|\s)\\?@([a-zA-Z0-9_]{0,24})$/.exec(before);
    if (!m) return setMention(null);
    const q = m[2];
    const start = caret - q.length - 1;
    void mentionSource(q).then((items) => setMention(items.length ? { q, start, items, index: 0 } : null));
  };

  const pickMention = (u: UserSummary) => {
    if (!mention) return;
    const caret = ref.current?.selectionStart ?? text.length;
    const next = `${text.slice(0, mention.start)}@${u.username} ${text.slice(caret)}`;
    setText(next);
    setMention(null);
    requestAnimationFrame(() => {
      const pos = mention.start + u.username.length + 2;
      ref.current?.setSelectionRange(pos, pos);
      ref.current?.focus();
    });
  };

  const sendExtra = (extra: Pick<ComposerSend, "gif" | "poll">) => {
    if (cooldown > 0) return;
    onSend({ content: "", mediaIds: [], ...extra });
    if (slowModeSeconds > 0) setCooldown(slowModeSeconds);
    ref.current?.focus();
  };

  /** `\@name` / `\@Role` / `\#channel` → raw syntax (`<@publicId>`, `<@&roleId>`, `<#channelId>`). */
  const withRawRefs = async (content: string) => {
    if (!/\\[@#]/.test(content)) return content;
    const users = new Map<string, string>();
    await Promise.all(
      escapedUsernames(content).map(async (name) => {
        const fromSource = (await mentionSource?.(name).catch(() => []))?.find((u) => u.username.toLowerCase() === name && u.publicId);
        const publicId = fromSource?.publicId ?? (await api.get<UserProfile>(`/api/users/${encodeURIComponent(name)}`).catch(() => null))?.publicId;
        if (publicId) users.set(name, publicId);
      }),
    );
    return expandRawRefs(content, { users, roles: rawRefs?.roles ?? [], channels: rawRefs?.channels ?? [] });
  };

  const send = () => {
    const content = text.trim();
    if ((!content && uploads.mediaIds.length === 0) || uploads.uploading || cooldown > 0 || content.length > MESSAGE_MAX) return;
    const mediaIds = uploads.mediaIds;
    sendChain.current = sendChain.current.then(async () => {
      const expanded = await withRawRefs(content);
      onSend({ content: expanded.length > MESSAGE_MAX ? content : expanded, mediaIds });
    }).catch(() => {});
    setText("");
    setSel(null);
    drafts.delete(draftKey);
    uploads.reset();
    if (slowModeSeconds > 0) setCooldown(slowModeSeconds);
  };

  const togglePreview = () => {
    previewOn = !preview;
    setPreview(previewOn);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (picker) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const d = e.key === "ArrowDown" ? 1 : -1;
        setCmdIndex((pickerIndex + d + picker.length) % picker.length);
        return;
      }
      if ((e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) || e.key === "Tab") {
        e.preventDefault();
        enterCommand(picker[pickerIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setPickerDismissed(true);
        return;
      }
    }
    if (mention) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const d = e.key === "ArrowDown" ? 1 : -1;
        setMention({ ...mention, index: (mention.index + d + mention.items.length) % mention.items.length });
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        pickMention(mention.items[mention.index]);
        return;
      }
      if (e.key === "Escape") {
        setMention(null);
        return;
      }
    }
    const shortcut = formatShortcut(e.nativeEvent, prefs.keybinds);
    if (shortcut) {
      e.preventDefault();
      e.stopPropagation();
      if (shortcut === "preview") togglePreview();
      else format(shortcut);
      return;
    }
    if (e.key === "Escape" && replyTo) onCancelReply();
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      // Ctrl/Cmd + Enter always sends; plain Enter sends only when "Enter to send" is on
      // (and never on touch keyboards, where Enter is the only way to add a line).
      const modified = e.ctrlKey || e.metaKey;
      if (modified || (prefs.enterToSend && window.matchMedia("(pointer: fine)").matches)) {
        e.preventDefault();
        send();
      }
    }
  };

  const onPaste = (e: ClipboardEvent) => {
    if (!allowAttachments) return;
    const files = pastedImages(e.clipboardData);
    if (files.length) {
      e.preventDefault();
      uploads.add(files);
    }
  };

  // Images pasted anywhere on the page (nothing else focused) or dropped onto it attach here.
  const [dragging, setDragging] = useState(false);
  const addUploads = uploads.add;
  useEffect(() => {
    if (!allowAttachments || disabledReason) return;
    const editable = (el: Element | null) => el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || (el instanceof HTMLElement && el.isContentEditable);
    // A dialog (settings, image editor…) owns paste and drop while it's open.
    const blocked = () => !ref.current || !!document.querySelector('[role="dialog"]');
    const attach = (files: File[]) => {
      addUploads(files);
      ref.current?.focus();
    };
    const onWindowPaste = (e: globalThis.ClipboardEvent) => {
      if (e.defaultPrevented || editable(document.activeElement) || blocked()) return;
      const files = pastedImages(e.clipboardData);
      if (!files.length) return;
      e.preventDefault();
      attach(files);
    };
    let depth = 0;
    const onEnter = (e: DragEvent) => {
      if (!isFileDrag(e.dataTransfer) || blocked()) return;
      depth++;
      setDragging(true);
    };
    const onLeave = (e: DragEvent) => {
      if (!isFileDrag(e.dataTransfer)) return;
      depth = Math.max(0, depth - 1);
      if (!depth) setDragging(false);
    };
    const onOver = (e: DragEvent) => {
      if (isFileDrag(e.dataTransfer) && !blocked()) e.preventDefault();
    };
    const onDrop = (e: DragEvent) => {
      depth = 0;
      setDragging(false);
      if (e.defaultPrevented || !isFileDrag(e.dataTransfer) || blocked()) return;
      e.preventDefault();
      const files = imageFiles(e.dataTransfer);
      if (files.length) attach(files);
      else toast.error("Only images can be attached");
    };
    window.addEventListener("paste", onWindowPaste);
    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("dragover", onOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("paste", onWindowPaste);
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("drop", onDrop);
      setDragging(false);
    };
  }, [allowAttachments, disabledReason, addUploads]);

  const previewMsg = useMemo<ChatMessage>(
    () => ({
      id: "composer-preview",
      author: { id: me.id, username: me.username, displayName: me.displayName, avatarUrl: me.avatarUrl },
      content: text.trim(),
      createdAt: new Date().toISOString(),
      editedAt: null,
      replyTo: null,
      media: [],
    }),
    [text, me.id, me.username, me.displayName, me.avatarUrl],
  );

  if (disabledReason) {
    return (
      <div className="shrink-0 px-4 pb-4">
        <div className="flex h-11 items-center rounded-lg bg-surface px-4 text-sm text-fg-3">{disabledReason}</div>
      </div>
    );
  }

  const canSend = (text.trim().length > 0 || uploads.mediaIds.length > 0) && !uploads.uploading && cooldown === 0;
  const showToolbar = !!sel && sel.start !== sel.end && !pointerDown && !mention && !!ref.current;
  return (
    <div className="relative shrink-0 px-3 pb-1 md:px-4 safe-bottom">
      {dragging && (
        <div className="pointer-events-none absolute inset-x-3 bottom-1 top-0 z-20 flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-accent bg-bg-subtle/95 text-sm font-medium text-fg md:inset-x-4">
          <IconImage className="icon size-5 text-accent" aria-hidden />
          Drop images to attach
        </div>
      )}
      {picker && <CommandPicker items={picker} index={pickerIndex} onPick={enterCommand} onHover={setCmdIndex} />}
      {mention && !picker && (
        <div className="absolute bottom-full left-4 right-4 mb-1 overflow-hidden rounded-lg bg-surface p-1 shadow-pop animate-pop-in" role="listbox" aria-label="Mention suggestions">
          {mention.items.map((u, i) => (
            <button
              key={u.id}
              role="option"
              aria-selected={i === mention.index}
              onMouseDown={(e) => {
                e.preventDefault();
                pickMention(u);
              }}
              className={cn("flex h-10 w-full items-center gap-2.5 rounded-md px-2 text-left text-sm", i === mention.index ? "bg-surface-3" : "hover:bg-surface-2")}
            >
              <Avatar src={u.avatarUrl} name={u.displayName} size="sm" />
              <span className="font-medium text-fg">{u.displayName}</span>
              <NameTags user={u} />
              <span className="text-fg-3">@{u.username}</span>
            </button>
          ))}
        </div>
      )}
      {showToolbar && ref.current && <FormatToolbar textarea={ref.current} selection={sel!} active={activeFormats(text, sel!.start, sel!.end)} onFormat={format} />}
      <div className="rounded-lg bg-surface focus-within:ring-1 focus-within:ring-line-strong">
        {replyTo && (
          <div className="flex items-center gap-2 border-b border-line px-3 py-1.5 text-xs text-fg-3">
            <span className="flex-1 truncate">
              Replying to <span className="font-semibold text-fg-2">{replyTo.author.displayName}</span>
            </span>
            <button onClick={onCancelReply} aria-label="Cancel reply" className="rounded p-0.5 hover:bg-surface-3 hover:text-fg">
              <IconX className="icon size-4" />
            </button>
          </div>
        )}
        {command && commands ? (
          <CommandForm
            key={`${command.applicationId}:${command.name}`}
            command={command}
            ctx={commands}
            onExit={exitCommand}
            onSent={(c) => {
              setSentTo(c);
              exitCommand();
            }}
          />
        ) : (
          <>
        {preview && text.trim() && (
          <section aria-label="Message preview" className="max-h-60 overflow-y-auto border-b border-line pb-2">
            <MessageItem msg={previewMsg} grouped={false} meId={me.id} meUsername={me.username} actions={PREVIEW_ACTIONS} preview />
          </section>
        )}
        {uploads.items.length > 0 && (
          <div className="px-3">
            <UploadPreviews items={uploads.items} onRemove={uploads.remove} />
          </div>
        )}
        <div className="flex items-end gap-1 py-1.5 pl-1.5 pr-3 md:pr-1.5">
          {allowAttachments && (
            <>
              <input
                ref={fileRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) uploads.add(e.target.files);
                  e.target.value = "";
                }}
              />
              <IconButton label="Attach images" icon={IconImage} onClick={() => fileRef.current?.click()} />
            </>
          )}
          <textarea
            ref={ref}
            value={text}
            rows={1}
            maxLength={MESSAGE_MAX}
            placeholder={placeholder}
            aria-label={placeholder}
            aria-controls={picker ? "command-suggestions" : undefined}
            aria-activedescendant={picker ? `command-option-${pickerIndex}` : undefined}
            onChange={(e) => {
              const v = e.target.value;
              // "/roll " (an exact command name and a space) goes straight into command mode.
              const exact = commands && !pickerDismissed && /^\/([\w-]+) $/.exec(v);
              const hit = exact && commandList ? (picker?.[pickerIndex]?.name === exact[1] ? picker[pickerIndex] : commandList.find((c) => c.name === exact[1])) : null;
              if (hit) return enterCommand(hit);
              setText(v);
              detectMention(v, e.target.selectionStart);
              if (onTyping && prefs.sendTyping && e.target.value && Date.now() - lastTyping.current > 3000) {
                lastTyping.current = Date.now();
                onTyping();
              }
            }}
            onSelect={syncSelection}
            onFocus={syncSelection}
            onPointerDown={() => setPointerDown(true)}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onBlur={() => {
              setSel(null);
              setTimeout(() => setMention(null), 150);
            }}
            className="max-h-60 min-h-9 flex-1 resize-none bg-transparent px-1.5 py-[7px] text-base leading-[1.4rem] text-fg placeholder:text-fg-3 focus:outline-none"
          />
          {allowPolls && <IconButton label="Create a poll" icon={IconPoll} onClick={() => setPollOpen(true)} disabled={cooldown > 0} />}
          {gifsEnabled && (
            <GifPicker tooltip="GIFs" open={gifOpen} onOpenChange={setGifOpen} onPick={(gif) => sendExtra({ gif })}>
              <IconButton label="GIFs" tooltip={false} icon={IconGif} active={gifOpen} disabled={cooldown > 0} />
            </GifPicker>
          )}
          <IconButton
            label={preview ? "Hide preview" : "Preview"}
            shortcut={PREVIEW_SHORTCUT}
            icon={preview ? IconEyeOff : IconEye}
            active={preview}
            onClick={togglePreview}
            className="hidden sm:inline-flex"
          />
          {cooldown > 0 ? (
            <span className="flex h-9 w-9 shrink-0 flex-col items-center justify-center text-[10px] leading-none text-fg-3 tabular" aria-live="polite">
              <IconHourglass className="icon size-4" aria-hidden />
              {cooldown}s
            </span>
          ) : (
            <IconButton label="Send" icon={IconSend} onClick={send} disabled={!canSend} className={cn("shrink-0", canSend && "text-fg")} />
          )}
        </div>
          </>
        )}
      </div>
      {allowPolls && <PollComposer open={pollOpen} onOpenChange={setPollOpen} onSubmit={(poll) => sendExtra({ poll })} />}
      {sentTo ? (
        <div className="flex h-5 items-center gap-1.5 px-1 text-xs text-fg-2" role="status">
          <IconSlashCommand className="icon size-3.5 shrink-0 text-fg-3" aria-hidden />
          <span className="truncate">
            Sent <span className="font-mono">/{sentTo.name}</span> to {sentTo.bot.displayName}…
          </span>
          <BotTag className="!h-4 !px-1" />
        </div>
      ) : (
        <TypingLine names={prefs.showTyping ? typingNames : []} />
      )}
    </div>
  );
}
